import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { Property } from '../../../lib/supabase';

const PLACEHOLDER_IMGS = [
  'https://public.readdy.ai/ai/img_res/04d86ac4e8b2bc4bebf646b2da0faef6.jpg',
  'https://readdy.ai/api/search-image?query=cozy%20living%20room%20apartment%20warm%20lighting%20comfortable%20furniture%20clean%20simple%20interior%20professional%20real%20estate%20photo%20neutral%20background&width=600&height=400&seq=sim_ph2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=contemporary%20apartment%20bedroom%20natural%20light%20clean%20design%20simple%20furniture%20white%20walls%20professional%20photography&width=600&height=400&seq=sim_ph3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=bright%20airy%20studio%20apartment%20with%20large%20windows%20modern%20decor%20professional%20real%20estate%20photography%20minimalist%20interior&width=600&height=400&seq=sim_ph4&orientation=landscape',
];

function getPlaceholder(id: string) {
  return PLACEHOLDER_IMGS[id.charCodeAt(0) % PLACEHOLDER_IMGS.length];
}

interface Props {
  currentProperty: Property;
}

export default function SimilarPropertiesSection({ currentProperty }: Props) {
  const [similar, setSimilar] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSimilar();
  }, [currentProperty.id]);

  const fetchSimilar = async () => {
    setLoading(true);
    const city = currentProperty.location.slice(0, 3);
    const priceMin = Math.floor(currentProperty.price * 0.6);
    const priceMax = Math.ceil(currentProperty.price * 1.6);

    // 優先：同城市 + 相近價位
    const { data: cityData } = await supabase
      .from('properties')
      .select('*')
      .eq('status', 'active')
      .neq('id', currentProperty.id)
      .ilike('location', `${city}%`)
      .gte('price', priceMin)
      .lte('price', priceMax)
      .order('created_at', { ascending: false })
      .limit(4);

    let results = cityData || [];

    // 不足 4 筆則補入同城市其他房源
    if (results.length < 4) {
      const existingIds = results.map((p) => p.id);
      const { data: extraData } = await supabase
        .from('properties')
        .select('*')
        .eq('status', 'active')
        .neq('id', currentProperty.id)
        .ilike('location', `${city}%`)
        .not('id', 'in', `(${[currentProperty.id, ...existingIds].join(',')})`)
        .order('created_at', { ascending: false })
        .limit(4 - results.length);
      results = [...results, ...(extraData || [])];
    }

    setSimilar(results.slice(0, 4));
    setLoading(false);
  };

  if (!loading && similar.length === 0) return null;

  return (
    <section className="mt-10 md:mt-16 px-4 md:px-0">
      {/* Section header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-xl">
            <i className="ri-home-search-line text-teal-600 text-base"></i>
          </div>
          <div>
            <h2 className="text-base font-black text-gray-900">相似房源推薦</h2>
            <p className="text-xs text-gray-400">
              {currentProperty.location.slice(0, 3)} · 相近價位房源
            </p>
          </div>
        </div>
        <Link
          to="/regions"
          className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap transition-colors"
        >
          查看更多
          <i className="ri-arrow-right-line text-xs"></i>
        </Link>
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
              <div className="w-full h-32 bg-gray-100"></div>
              <div className="p-3 space-y-2">
                <div className="h-3 bg-gray-100 rounded w-3/4"></div>
                <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                <div className="h-4 bg-gray-100 rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {similar.map((prop) => {
            const coverImg =
              prop.photos && prop.photos.length > 0
                ? prop.photos[0]
                : getPlaceholder(prop.id);
            const imgPos =
              prop.photo_positions && prop.photo_positions.length > 0
                ? prop.photo_positions[0]
                : '50% 50%';

            return (
              <Link
                key={prop.id}
                to={`/property/${prop.id}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-teal-200 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer block"
              >
                {/* Cover image */}
                <div className="relative w-full overflow-hidden bg-gray-100" style={{ aspectRatio: '4/3' }}>
                  <img
                    src={coverImg}
                    alt={prop.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    style={{ objectPosition: imgPos }}
                    loading="lazy"
                  />
                  {/* Tag */}
                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-teal-500 text-white text-[10px] font-semibold rounded-full whitespace-nowrap">
                    屋主直聯
                  </span>
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="text-xs font-semibold text-gray-800 line-clamp-2 leading-snug mb-1.5 group-hover:text-teal-700 transition-colors">
                    {prop.title}
                  </p>

                  <div className="flex items-center gap-1 text-[10px] text-gray-400 mb-2">
                    <i className="ri-map-pin-line text-teal-400 text-xs flex-shrink-0"></i>
                    <span className="truncate">{prop.location}</span>
                  </div>

                  {/* Specs row */}
                  <div className="flex items-center gap-2 text-[10px] text-gray-500 mb-2">
                    <span className="flex items-center gap-0.5">
                      <i className="ri-expand-2-line text-xs"></i>
                      {prop.area}坪
                    </span>
                    <span className="w-px h-2.5 bg-gray-200"></span>
                    <span className="flex items-center gap-0.5">
                      <i className="ri-door-open-line text-xs"></i>
                      {prop.rooms}房{prop.bathrooms}衛
                    </span>
                  </div>

                  {/* Price */}
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="text-sm font-black text-red-500">
                        NT$ {prop.price.toLocaleString()}
                      </span>
                      <span className="text-[10px] text-gray-400">/月</span>
                    </div>
                    <div className="w-6 h-6 flex items-center justify-center bg-teal-50 rounded-lg group-hover:bg-teal-500 transition-colors">
                      <i className="ri-arrow-right-line text-[10px] text-teal-500 group-hover:text-white transition-colors"></i>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Bottom link */}
      <div className="mt-4 text-center">
        <Link
          to="/regions"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 hover:border-teal-300 hover:bg-teal-50 text-gray-600 hover:text-teal-600 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-search-line text-sm"></i>
          探索更多{currentProperty.location.slice(0, 3)}房源
          <i className="ri-arrow-right-line text-sm"></i>
        </Link>
      </div>
    </section>
  );
}
